import { DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { FormGroup, FormControl, Validators, FormArray} from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, BehaviorSubject, Observable, of } from 'rxjs';
import { triggerFormValidation, focusFirstInvalidField, scrollToHeader, isNullOrUndefined} from '@app/base/shared';
import { ConstantsService, SessionService, RequestTypesEnum, WizardStep, StepsIdEnum,
  StepsTitleMixedUseEnum, StepsCodeEnum, StepsRouteEnum, StepsResidentialTitleEnum,
  LookupsService, ApplicantMessageEnum, Type, GasRequestStatus } from '@app/base/core';
import { SocoModalService } from '@app/base/soco';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { GasRequestModel } from '@app/base/shared/models/applicant/new.model';
import { GasRequestNumber } from '@app/base/shared/models/applicant/gas-request-number';
import { CompaniesEnum, QuestionAnswers, StepsTitleCommercialEnum } from '@app/core/models';
import { SocoHttpService, ApiResult } from '@app/base/soco';

declare var swal: any;
require('sweetalert');

/* istanbul ignore next */
@Injectable()
export class MultistepsService {
  //#region => Variables & Properties
  // Session state related variables
  private paramRequestTypeSource = new Subject();
  public paramRequestTypeResolved$ = this.paramRequestTypeSource.asObservable();
  public paramRequestType: string = RequestTypesEnum.Residential;   // Hold the request type, e.g. commercial

  public duplicatedRequest = false; // Meter existed, or duplicated record in database, etc.
  public turnOnRequest = false;  // If user got a meter and just want to turn on service.
  public requestValidated = false; // Request validated by calling ALM & check BCA table for duplicate
  public customerRequested = false; // true if customer edits service address

  public meterDuplicate: Type = new Type();
  public bcaDuplicate: GasRequestStatus = new GasRequestStatus(); // Hold the result of BCA duplicate check

  public steps: WizardStep[] = new Array<WizardStep>(); // Hold step data and forms
  public maxCompletedStep = -1; // How far user progressed in multistep wizard
  public isWizardCompleted = false;  // Indicate if wizard completed, all steps verified and submitted to server.

  // Monitor active step and notify listener when active step change
  private activeStep: StepsIdEnum;  // The current active step
  private activeStepSource = new BehaviorSubject<StepsIdEnum>(StepsIdEnum.LOCATION);
  public activeStepChanged$ = this.activeStepSource.asObservable();
  public serviceCounty: any = null;
  // Hold service address
  public serviceAddress: any = null;
  public mailingAddress: any = null;
  public customerAddress: any = null;
  public addressValidationDone = false;
  public addressChanged = false;

  // Hold Gas Request Model
  public MainModel = new GasRequestModel();
  public resPremiseQuestions: QuestionAnswers[] = [];
  public comPremiseQuestions: QuestionAnswers[] = [];
  public restypeQuestions: QuestionAnswers[] = [];
  public comtypeQuestions: QuestionAnswers[] = [];


  get hasNextStep(): boolean { return this.activeStep < StepsIdEnum.ACKNOWLEDGE; }
  get hasPreviousStep(): boolean { return this.activeStep > StepsIdEnum.LOCATION; }
  get getActiveStep(): WizardStep { return this.steps.find(x => x.id === this.activeStep); }

  // Hold te Applicant Meesage based on type component
  public paramApplicantMessage: string = ApplicantMessageEnum.Message2c;   // Hold the request type, e.g. commercial
  //#endregion

  //#region => CTOR
  constructor(
    private router: Router,
    private socoModalService: SocoModalService,
    private constantsSvc: ConstantsService,
    private sessionSvc: SessionService,
    private lookupSvc: LookupsService,
    private http: SocoHttpService, private datePipe: DatePipe) {
    this.resetWizardState();
    this.listenForStepChanges();
    this.paramRequestTypeResolved$.subscribe(() => {
        this.initForms();
     });
  }
  //#endregion

  //#region => Wizard State
  // No longer needed to reset wizard state, wizard service is provided on outlet scope, once component destroyed service also destroyed
  // Only needed in acknowledge step to reset data.
  public resetWizardState(): void {
    this.maxCompletedStep = -1;
    this.steps.forEach(f => { f.form.reset(); });
    this.lookupSvc.bcaDuplicate = null;
    this.lookupSvc.meterDuplicate = null;
  }
  public paramRequestTypeResolved() {
    this.paramRequestTypeSource.next();
  }
  //#endregion

  //#region => Wizard Navigation
  public nextStep(): void { if (this.hasNextStep) { this.goToStep(this.activeStep + 1); } }
  public previousStep(): void { if (this.hasPreviousStep) { this.goToStep(this.activeStep - 1); } }
  public submitWizard(): void {
    if (!this.validateApplicationIntegrity()) { // One of the steps got invalid form, loop through and trigger a validation SWAL.
      this.steps.some((step) => {
        if (!step.form.valid) {
          this.showDiscardDialog(`Something is wrong with "${step.label}", would you like to fix it?`, 'Later', 'Fix Now', true);
          const subscription = this.socoModalService.modalConfirmation$.subscribe((fixNow) => {
            subscription.unsubscribe();
            if (fixNow) { this.goToStep(step.id); }
          });
          return true;
        }
      });
    } else {
      // Submit to server and navigate to acknowledge
      this.nextStep(); // If step is valid, then go to next step.
    }
  }
  public goToStep(step: StepsIdEnum): void {
    // Check for Duplicate Request based on type step questions and answers, meter exist and bca exist on serviceable address
    this.checkDuplicatedRequest();

    // If request is duplicated, mark Review as the next step
    if (this.duplicatedRequest) { // Handle duplicated requests scenario
      if (this.activeStep === StepsIdEnum.TYPE && step > StepsIdEnum.TYPE) {
        step = StepsIdEnum.REVIEW;
      }
      if (this.activeStep === StepsIdEnum.REVIEW && step < StepsIdEnum.REVIEW) {
        step = (step === StepsIdEnum.LOCATION) ? StepsIdEnum.LOCATION : StepsIdEnum.TYPE;
      }
    }

    if (this.paramRequestType === RequestTypesEnum.MixedUse) {
      // Handle the Mixed Use scenario to hide Type -> TODO: fill the type to avoid Validation Error.
      if (this.activeStep === StepsIdEnum.LOCATION && step > StepsIdEnum.LOCATION) { step = StepsIdEnum.INFORMATION; }
      if (this.activeStep === StepsIdEnum.INFORMATION && step < StepsIdEnum.INFORMATION) { step = StepsIdEnum.LOCATION; }
    }

    triggerFormValidation(this.getActiveStep.form);
    focusFirstInvalidField(this.getActiveStep.form);
    this.getActiveStep.validStep = this.getActiveStep.form.valid;
    this.routeToStep(this.getStepById(step));
  }
  public routeToStep(step: WizardStep): void {
    // User clicked on Form submit from review page and trying to create a request
    if ((step.id - 1) === StepsIdEnum.REVIEW) {
      this.TransformModel(step);
    } else {
      this.router.navigate([this.sessionSvc.paramLdc.toLowerCase(), 'applicant', this.paramRequestType.toLocaleLowerCase(), step.route]);
    }
  }
  public getStepById(step: StepsIdEnum): WizardStep {
    return this.steps.find(x => x.id === step);
  }
  public canDeactivateStep(route: ActivatedRouteSnapshot, state: RouterStateSnapshot, nextState: RouterStateSnapshot): Observable<boolean> {
    const resolvedStep = this.steps.find(x => x.route.toUpperCase() === route.url[0].path.toUpperCase());
    const fromInsideWizard = state.url.indexOf('applicant') > -1;
    const toInsideWizard = nextState.url.indexOf('applicant') > -1;
    const fromAcknowledgeStep = state.url.indexOf(StepsRouteEnum.ACKNOWLEDGE) > -1;
    const toOutsideWizard = nextState.url.indexOf('applicant') === -1;

    // User reached review step with duplicated request and navigating to home paand navigating to home page.
    if (this.duplicatedRequest && resolvedStep.code === StepsCodeEnum.REVIEW && toOutsideWizard) { return of(true); }

    if (!this.isWizardCompleted && (fromInsideWizard && toOutsideWizard)) {
      // Leaving Wizard to some other page (Outside wizard scope)
      this.showDiscardDialog(this.constantsSvc.showDiscardmsgMultistep, 'Stay', 'Discard Changes', true);
      return this.socoModalService.modalConfirmation$;
    } else if (this.isWizardCompleted && ((fromInsideWizard && fromAcknowledgeStep) && toInsideWizard)) {
      // Prevent navigation back to wizard once reached acknowledge
      this.navigateToHome();
      return of(true);
    } else if (!this.isWizardCompleted && !resolvedStep.form.valid && fromInsideWizard && toInsideWizard && this.maxCompletedStep > 0) {
      // Moving between steps with invalid data
      this.showDiscardDialog(this.constantsSvc.showDiscardmsgInvalidDataMultistep, 'Fix Now', 'Fix Later', false);
      const subscription = this.socoModalService.modalConfirmation$.subscribe(choice => {
        subscription.unsubscribe();
        if (!choice) { this.goToStep(resolvedStep.id); }
      });
      return this.socoModalService.modalConfirmation$;
    } else { return of(true); }

  }
  public navigateToHome() {
    this.router.navigate([this.sessionSvc.paramLdc.toLowerCase(), 'home']); // Navigates to Home page
  }
  // Dialog box for confirmation to proceed or discard changes
  private showDiscardDialog(body: string, cancelText: string, confirmText: string, isDanger: boolean) {
    this.socoModalService.confirm({
      title: 'Confirmation',
      body: body,
      headerCssClass: isDanger ? 'bg-danger' : 'bg-info',
      buttonCssClass: isDanger ? 'btn-danger' : 'btn-info',
      confirmButton: confirmText,
      cancelButton: cancelText
    });
  }
  private listenForStepChanges() {
     this.activeStepChanged$.subscribe(activeStep => {
       this.activeStep = activeStep;
      this.maxCompletedStep = this.maxCompletedStep > this.activeStep ? this.maxCompletedStep :
        this.activeStep;  // How far we got in the wizard to sync with wizard icons.
    });  // Keep a local copy of activeStep
  }
  public syncActiveStep(step: StepsIdEnum) {
    // On step deactivate, if validation faild, we need to revert the current active step.
    this.activeStepSource.next(step);
    scrollToHeader();
  }
  //#endregion

  //#region => Forms Validation
  // Validate step's forms, pass nullable step id to validate it's form. If no step id provided then validate all steps forms. */
  public validateApplicationIntegrity(step?: StepsIdEnum): boolean {
    return step // Validate a specific form?
      ? this.steps.find(x => x.id === step).form.valid // Developer passed a specific form to validate
      : this.steps.every(x => x.form.valid);
  }
  //#endregion


  /*public disableChecks(): boolean {
    if (this.sessionSvc.paramLdc === CompaniesEnum.GA && this.paramRequestType.toUpperCase() == RequestTypesEnum.Residential) {
      if (this.activeStep === StepsIdEnum.TYPE) {
        if(this.steps[StepsIdEnum.TYPE].form.value.question62 === this.constantsSvc.Yes || this.steps[StepsIdEnum.TYPE].form.value.question62 === this.constantsSvc.No){
          return true;
        }
        else {
          return false;
        }
      }
      else if(this.activeStep === StepsIdEnum.PREMISE) {
        if(this.steps[StepsIdEnum.PREMISE].form.value.marketerChosen === this.constantsSvc.Yes || this.steps[StepsIdEnum.PREMISE].form.value.marketerChosen === this.constantsSvc.No){
          if(this.steps[StepsIdEnum.PREMISE].form.getRawValue().marketerChoice != null) {
            return true;
          }
          else {
            return false;
          }
        }
        else{
          return false;
        }
      }
      else {
        return true;
      }
    }
    else {
      return true;
    }
  }*/
  //#endregion

  //#region => Disable 'Next' button
  //Disable Next button when "Are you a licensed builder"/ "Have you chosen a Marketer" is not answered
  public disableCheck(): boolean {
    if (this.activeStep === StepsIdEnum.TYPE) {
      if(this.steps[StepsIdEnum.TYPE].form.value.question62 === this.constantsSvc.Yes || this.steps[StepsIdEnum.TYPE].form.value.question62 === this.constantsSvc.No){
        return true;
      }
      else {
        return false;
      }
    }
    else if(this.activeStep === StepsIdEnum.PREMISE) {
      if (this.sessionSvc.paramLdc === CompaniesEnum.GA) {
        if(this.paramRequestType.toUpperCase() == RequestTypesEnum.Residential) {
          if(this.steps[StepsIdEnum.PREMISE].form.value.marketerChosen === this.constantsSvc.Yes || this.steps[StepsIdEnum.PREMISE].form.value.marketerChosen === this.constantsSvc.No) {
            if(this.steps[StepsIdEnum.PREMISE].form.getRawValue().marketerChoice != null) {
              return true;
            }
            else {
              return false;
            }
          }
          else{
            return false;
          }
        }
        else if (this.paramRequestType.toUpperCase() == RequestTypesEnum.Commercial) {
          if(this.steps[StepsIdEnum.PREMISE].form.value.commercialMarketerChosen === this.constantsSvc.Yes || this.steps[StepsIdEnum.PREMISE].form.value.commercialMarketerChosen === this.constantsSvc.No) {
            if(this.steps[StepsIdEnum.PREMISE].form.getRawValue().commercialMarketerChoice != null) {
              return true;
            }
          }
          else{
            return false;
          }
        }
      }
      else {
        return true;
      }
    }
    else {
      return true;
    }
  }
  //#endregion

  //#region => Forms Definition
  private initForms(): void {
    this.steps.push({
      id: StepsIdEnum.LOCATION,
      code: StepsCodeEnum.LOCATION,
      label: this.getStepTitle(StepsCodeEnum.LOCATION),
      title: this.getStepTitle(StepsCodeEnum.LOCATION),
      route: StepsRouteEnum.LOCATION,
      validStep: true,
      form: new FormGroup({
        locationStreetNumber: new FormControl(null, [Validators.pattern(this.constantsSvc.numericPatternwithZero)]),
        locationStreetName: new FormControl(null),
        locationCSZ: new FormControl(null),
      })
    });

    this.steps.push({
      id: StepsIdEnum.TYPE,
      code: StepsCodeEnum.TYPE,
      label: this.getStepTitle(StepsCodeEnum.TYPE),
      title: this.getStepTitle(StepsCodeEnum.TYPE),
      route: StepsRouteEnum.TYPE,
      validStep: true,
        form: this.paramRequestType.toUpperCase() === RequestTypesEnum.Residential ? new FormGroup({
        question62: new FormControl(''),
        question1: new FormControl(null, [Validators.required]),
        question2: new FormControl(null, [Validators.required]),
        question3: new FormControl(null, [Validators.required]),
        question4: new FormControl(null, [Validators.required]),
        question5: new FormControl(null, [Validators.required]),
        question6: new FormControl(null, [Validators.required]),
        question7: new FormControl(null, [Validators.required]),
        question40: new FormControl(null, [Validators.required]),
        question60: new FormControl(null, [Validators.required])
      }) : this.paramRequestType.toUpperCase() === RequestTypesEnum.Commercial ?
          new FormGroup({
            question62: new FormControl(''),
            question16: new FormControl(null, [Validators.required]),
            question17: new FormControl(null, [Validators.required]),
            question18: new FormControl(null, [Validators.required]),
            question19: new FormControl(null, [Validators.required]),
            question20: new FormControl(null, [Validators.required]),
            question21: new FormControl(null, [Validators.required]),
          }) : new FormGroup({})
    });

    this.steps.push({
      id: StepsIdEnum.INFORMATION,
      code: StepsCodeEnum.INFORMATION,
      label: this.getStepTitle(StepsCodeEnum.INFORMATION),
      title: this.getStepTitle(StepsCodeEnum.INFORMATION),
      route: StepsRouteEnum.INFORMATION,
      validStep: true,
      form: new FormGroup({
        firstName: new FormControl('', [Validators.required, Validators.pattern(this.constantsSvc.spacePattern)]),
        middleInitial: new FormControl(''),
        lastName: new FormControl('', [Validators.required, Validators.pattern(this.constantsSvc.spacePattern)]),
        emailAddress: new FormControl('', [Validators.required, Validators.pattern(this.constantsSvc.emailPattern)]),
        phoneNumberType: new FormControl(null, [Validators.required]),
        applicantPhoneNumber: new FormControl('', [Validators.required, Validators.pattern(this.constantsSvc.phoneNumberPattern)]),
        applicantPhoneNumberExtension:
          new FormControl('', [Validators.pattern(this.constantsSvc.phoneNumberExtensionPattern), Validators.maxLength(6)]),
        companyName: new FormControl(''),
        isLiketoReceiveTestMessages: new FormControl(''),
        applicantPhoneNumber1: new FormControl(''),
        applicantPhoneNumber2: new FormControl('', Validators.pattern(this.constantsSvc.phoneNumberPattern)),
        applicantPhoneNumber3: new FormControl('', Validators.pattern(this.constantsSvc.phoneNumberPattern)),
        // Site Contact Information
        siteContactSame: new FormControl(''),
        contactTypeDetails: new FormArray([new FormGroup({
          companyName: new FormControl(''),
          firstName: new FormControl('', [Validators.required, Validators.pattern(this.constantsSvc.spacePattern)]),
          lastName: new FormControl('', [Validators.required, Validators.pattern(this.constantsSvc.spacePattern)]),
          siteContactPhoneNumber: new FormControl('', [Validators.required, Validators.pattern(this.constantsSvc.phoneNumberPattern)]),
          siteContactPhoneNumberExtension:
            new FormControl('', [Validators.pattern(this.constantsSvc.phoneNumberExtensionPattern), Validators.maxLength(6)]),
          emailAddress: new FormControl('', [Validators.required, Validators.pattern(this.constantsSvc.emailPattern)]),
          siteContactType: new FormControl(null, [Validators.required]),
          siteContactTypeOthers: new FormControl(null)
        }, { updateOn: 'blur' })]),
          kmiCommunication: new FormControl(null, [Validators.required]),
          kmiFrequency: new FormControl(null, [Validators.required]),
          kmiReceive: new FormControl(null, [Validators.required]),
          kmiEmailAddress: new FormControl(null, {validators: [Validators.required, Validators.pattern(this.constantsSvc.emailPattern)], updateOn: 'blur'}),
          kmiTextNumber: new FormControl('', {validators: [Validators.required, Validators.pattern(this.constantsSvc.phoneNumberPattern)], updateOn: 'blur'})
      })
    });

    this.steps.push({
      id: StepsIdEnum.ADDRESSES,
      code: StepsCodeEnum.ADDRESSES,
      label: this.getStepTitle(StepsCodeEnum.ADDRESSES),
      title: this.getStepTitle(StepsCodeEnum.ADDRESSES),
      route: StepsRouteEnum.ADDRESSES,
      validStep: true,
      form: new FormGroup({
        // Service Address Information
        serviceStreetNumber:
          new FormControl({ value: null, disabled: true }, [Validators.required,
          Validators.pattern(this.constantsSvc.numericPatternwithZero)]),
        serviceStreetName: new FormControl({ value: null, disabled: true }, [Validators.required]),
        serviceUnitNumber: new FormControl('', [Validators.pattern(this.constantsSvc.alphaNumericPattern)]),
        serviceState: new FormControl({ value: null, disabled: true }, [Validators.required]),
        serviceCity: new FormControl({ value: null, disabled: true }, [Validators.required]),
        serviceZip:
          new FormControl({ value: null, disabled: true }, [Validators.required,
          Validators.pattern(this.constantsSvc.numericPattern)]),
        serviceDevelopementName: new FormControl('', [Validators.pattern(this.constantsSvc.alphaNumericPattern)]),
        serviceLanddistrict: new FormControl(''),
        serviceLandLot: new FormControl(''),
        serviceCounty: new FormControl(null),
        // Customer Requested Address Information
        customerStreetNumber:
          new FormControl({ value: null, disabled: true }, [Validators.required,
          Validators.pattern(this.constantsSvc.numericPatternwithZero)]),
        customerStreetName: new FormControl({ value: null, disabled: true }, [Validators.required]),
        customerUnitNumber: new FormControl('', [Validators.pattern(this.constantsSvc.alphaNumericPattern)]),
        customerState: new FormControl({ value: null, disabled: true }, [Validators.required]),
        customerCity: new FormControl({ value: null, disabled: true }, [Validators.required]),
        customerZip:
          new FormControl({ value: null, disabled: true }, [Validators.required, Validators.pattern(this.constantsSvc.numericPattern)]),
        customerDevelopementName: new FormControl('', [Validators.pattern(this.constantsSvc.alphaNumericPattern)]),
        customerLanddistrict: new FormControl(''),
        customerLandLot: new FormControl(''),
        customerCounty: new FormControl(null),
        // Mailing Address Information
        mailingAddressSame: new FormControl(''),
        isPoBox: new FormControl(),
        poBoxAddress: new FormControl(null, [Validators.pattern(this.constantsSvc.numericPatternwithZero)]),
        mailingStreetNumber: new FormControl('', [Validators.required, Validators.pattern(this.constantsSvc.numericPatternwithZero)]),
        mailingStreetName: new FormControl('', [Validators.required, Validators.pattern(this.constantsSvc.alphaNumericPattern)]),
        mailingUnitNumber: new FormControl('', [Validators.pattern(this.constantsSvc.alphaNumericPattern)]),
        mailingState: new FormControl({ value: null, disabled: true }, [Validators.required]),
        mailingCity: new FormControl({ value: null, disabled: true }, [Validators.required]),
        mailingZip: new FormControl('', [Validators.required, Validators.pattern(this.constantsSvc.zipCodePattern)]),
      },
        { updateOn: 'blur' }
      )
    });

    this.steps.push({
      id: StepsIdEnum.PREMISE,
      code: StepsCodeEnum.PREMISE,
      label: this.getStepTitle(StepsCodeEnum.PREMISE),
      title: this.getStepTitle(StepsCodeEnum.PREMISE),
      route: StepsRouteEnum.PREMISE,
      validStep: true,
      form: this.buildPremiseForm()
    });

    this.steps.push({
      id: StepsIdEnum.REVIEW,
      code: StepsCodeEnum.REVIEW,
      label: this.getStepTitle(StepsCodeEnum.REVIEW),
      title: this.getStepTitle(StepsCodeEnum.REVIEW),
      route: StepsRouteEnum.REVIEW,
      validStep: true,
      form: new FormGroup({})
    });

    this.steps.push({
      id: StepsIdEnum.ACKNOWLEDGE,
      code: StepsCodeEnum.ACKNOWLEDGE,
      label: this.getStepTitle(StepsCodeEnum.ACKNOWLEDGE),
      title: this.getStepTitle(StepsCodeEnum.ACKNOWLEDGE),
      route: StepsRouteEnum.ACKNOWLEDGE,
      validStep: true,
      form: new FormGroup({})
    });
  }
  // Form Definition for Premise based on PropertyType
  buildPremiseForm(): FormGroup {
    switch (this.paramRequestType) {
      case RequestTypesEnum.Residential: return new FormGroup({
        residentialHomeSqFt: new FormControl('', [Validators.required, Validators.pattern(this.constantsSvc.numericPattern)]),
        residentialApproxDistanceFt: new FormControl('', Validators.pattern(this.constantsSvc.numericPattern)),
        residentialDeliveryPressure: new FormControl(null, Validators.required),
        residentialNoOfUnit: new FormControl('', Validators.pattern(this.constantsSvc.numericPattern)),
        // TODO: change true to check for value of new construction in type step to 'Yes'
        residentialStatusofConstruction: true ? new FormControl(null, [Validators.required]) : new FormControl(''),
        residentialUnitDescription: new FormControl(null, Validators.required),
        residentialNeedingServiceDate: new FormControl('', [Validators.pattern(this.constantsSvc.dateValidator)]),
        // TODO: change true to check for value of selected serviceable state in location page to 'Florida'
        residentialPropertyOwner: new FormControl(''),
        // TODO: change true to check for value of residentialDeliveryPressure to 'Others'
        residentialDeliveryPressureOthers: new FormControl(''),
        marketerChosen: new FormControl(null),
        marketerChoice: new FormControl(null),
        appliancesforResidential: new FormArray([new FormGroup({
          defaultAppliance: new FormControl(null, [Validators.required]),
          defaultApplianceOther: new FormControl(null),
          quantity: new FormControl('', [Validators.required, Validators.pattern(this.constantsSvc.numericPattern)]),
          defaultValue: new FormControl('', [Validators.pattern(this.constantsSvc.numericPatternwithZero)]),
          existingLoad: new FormControl(null)
        }, { updateOn: 'blur' })])
      }, { updateOn: 'blur' });
      case RequestTypesEnum.Commercial: return new FormGroup({
        commercialHomeSqFt: new FormControl('', [Validators.required, Validators.pattern(this.constantsSvc.numericPattern)]),
        commercialApproxDistanceFt: new FormControl('', Validators.pattern(this.constantsSvc.numericPattern)),
        commercialDeliveryPressure: new FormControl(null, Validators.required),
        // TODO: change true to check for value of new construction in type step to 'Yes'
        commercialStatusofConstruction: new FormControl(null),
        commercialUnitDescription: new FormControl(null, Validators.required),
        commercialNeedingServiceDate: new FormControl('', [Validators.pattern(this.constantsSvc.dateValidator)]),
        // TODO: change true to check for value of commercialDeliveryPressure to 'Others'
        commercialDeliveryPressureOthers: new FormControl(''),
        commercialMarketerChosen: new FormControl(null),
        commercialMarketerChoice: new FormControl(null),
        appliancesforCommercial: new FormArray([new FormGroup({
          defaultAppliance: new FormControl(null, [Validators.required]),
          defaultApplianceOther: new FormControl(null),
          quantity: new FormControl('', [Validators.required, Validators.pattern(this.constantsSvc.numericPattern)]),
          defaultValue: new FormControl('', [Validators.required, Validators.pattern(this.constantsSvc.numericPatternwithZero)]),
          existingLoad: new FormControl(false)
        }, { updateOn: 'blur' })])
      }, { updateOn: 'blur' });
      case RequestTypesEnum.MixedUse: return new FormGroup({
        // Mixed Use Residential
        residentialHomeSqFt: new FormControl('', [Validators.required, Validators.pattern(this.constantsSvc.numericPattern)]),
        residentialApproxDistanceFt: new FormControl('', Validators.pattern(this.constantsSvc.numericPattern)),
        residentialDeliveryPressure: new FormControl(null, Validators.required),
        residentialNoOfUnit: new FormControl('', Validators.pattern(this.constantsSvc.numericPattern)),
        residentialStatusofConstruction: new FormControl(null, Validators.required),
        residentialUnitDescription: new FormControl(null, Validators.required),
        residentialNeedingServiceDate: new FormControl('', [Validators.pattern(this.constantsSvc.dateValidator)]),
        // TODO: change true to check for value of mixedUseResidentialDeliveryPressure to 'Others'
        residentialDeliveryPressureOthers: new FormControl(null),
        appliancesforResidential: new FormArray([new FormGroup({
          defaultAppliance: new FormControl(null, [Validators.required]),
          defaultApplianceOther: new FormControl(null),
          quantity: new FormControl('', [Validators.required, Validators.pattern(this.constantsSvc.numericPattern)]),
          defaultValue: new FormControl('', [Validators.pattern(this.constantsSvc.numericPatternwithZero)])
        }, { updateOn: 'blur' })]),

        // Mixed Use Commercial
        commercialHomeSqFt: new FormControl('', [Validators.required, Validators.pattern(this.constantsSvc.numericPattern)]),
        commercialApproxDistanceFt: new FormControl('', Validators.pattern(this.constantsSvc.numericPattern)),
        commercialDeliveryPressure: new FormControl(null, Validators.required),
        commercialStatusofConstruction: new FormControl(null, Validators.required),
        commercialUnitDescription: new FormControl(null, Validators.required),
        commercialNeedingServiceDate: new FormControl('', [Validators.pattern(this.constantsSvc.dateValidator)]),
        // TODO: change true to check for value of mixedUseCommercialDeliveryPressure to 'Others'
        commercialDeliveryPressureOthers: new FormControl(null),
        appliancesforCommercial: new FormArray([new FormGroup({
          defaultAppliance: new FormControl(null, [Validators.required]),
          defaultApplianceOther: new FormControl(null),
          quantity: new FormControl('', [Validators.required, Validators.pattern(this.constantsSvc.numericPattern)]),
          defaultValue: new FormControl('', [Validators.required, Validators.pattern(this.constantsSvc.numericPatternwithZero)])
        }, { updateOn: 'blur' })])
      }, { updateOn: 'blur' });
    }
  }
  //#endregion

  //#region => Helper Functions
  public getStepTitle(stepId: StepsCodeEnum): string {
    switch (this.paramRequestType) {
      case RequestTypesEnum.Residential: return StepsResidentialTitleEnum[stepId];
      case RequestTypesEnum.Commercial: return StepsTitleCommercialEnum[stepId];
      case RequestTypesEnum.MixedUse: return StepsTitleMixedUseEnum[stepId];
    }
  }
  //#endregion

  //#region => Set DuplicatedRequest and Message for Type Component
  // Called from gotostep to check for duplicatedRequest and setting up messages
  checkDuplicatedRequest() {
    if ((this.steps[StepsIdEnum.TYPE].form.value.question40 === 'Yes' ||
      this.steps[StepsIdEnum.TYPE].form.value.question4 === 'No' || this.steps[StepsIdEnum.TYPE].form.value.question19 === 'No') && (
        this.steps[StepsIdEnum.TYPE].form.value.question40 === 'Yes' ||
      this.steps[StepsIdEnum.TYPE].form.value.question18 === 'No' || this.steps[StepsIdEnum.TYPE].form.value.question3 === 'No')
      && this.activeStep === StepsIdEnum.TYPE && this.getActiveStep.form.valid) {
      this.duplicatedRequest = true;
      this.paramApplicantMessage = ApplicantMessageEnum.Message2c;
    } else if (this.activeStep === StepsIdEnum.TYPE && this.getActiveStep.form.valid) {
      const isBCAQuestionAddedLoad = Boolean(this.steps[StepsIdEnum.TYPE].form.value.question4 === 'Yes' ||
        this.steps[StepsIdEnum.TYPE].form.value.question19 === 'Yes' || this.steps[StepsIdEnum.TYPE].form.value.question18 === 'Yes' ||
        this.steps[StepsIdEnum.TYPE].form.value.question3 === 'Yes');
      if (this.lookupSvc.bcaDuplicate.isBCAFound && !this.lookupSvc.meterDuplicate.isMeterExist) {
        this.paramApplicantMessage = ApplicantMessageEnum.Message2a;
        this.duplicatedRequest = true;
      } else if (this.lookupSvc.bcaDuplicate.isBCAAddedLoad && this.lookupSvc.meterDuplicate.isMeterExist && isBCAQuestionAddedLoad) {
        this.paramApplicantMessage = ApplicantMessageEnum.Message2b;
        this.duplicatedRequest = true;
      } else if (this.lookupSvc.bcaDuplicate.isBCAFound && this.lookupSvc.meterDuplicate.isMeterExist && !isBCAQuestionAddedLoad) {
        this.paramApplicantMessage = ApplicantMessageEnum.Message2c;
        this.duplicatedRequest = true;
      } else if (!this.lookupSvc.bcaDuplicate.isBCAFound && this.lookupSvc.meterDuplicate.isMeterExist && !isBCAQuestionAddedLoad) {
        this.paramApplicantMessage = ApplicantMessageEnum.Message2d;
        this.duplicatedRequest = true;
      } else {
        this.paramApplicantMessage = '';
        if (this.duplicatedRequest) {
          // Handle scenario user reached review after detecting a duplicate, then went back to type and no duplicate detected.
          this.maxCompletedStep = StepsIdEnum.TYPE;
          this.duplicatedRequest = false;
        }
      }
    }
  }
  //#endregion
  // To get formatted mailing address
  getFormattedMailingAddress() {
    const address = this.steps[StepsIdEnum.ADDRESSES].form.getRawValue();
    const poBoxPrefix = this.constantsSvc.poBoxPrefix;
    const cityStateZipConcated = address.mailingCity.itemName + ' ,' + address.mailingState.id + ' ' + address.mailingZip;
    if (address.isPoBox) {
      return poBoxPrefix + ' ' + address.poBoxAddress + ', ' + cityStateZipConcated;
    } else {
      return address.mailingStreetName + address.mailingStreetNumber + ', ' + cityStateZipConcated;
    }
  }

  // To get formatted service address
  getFormattedServiceAddress() {
    const address = this.steps[StepsIdEnum.ADDRESSES].form.getRawValue();
    const cityStateZipConcated = address.serviceCity.itemName + ', ' + address.serviceState.id + ' ' + address.serviceZip;
    return address.customerStreetNumber + ' ' + address.customerStreetName +  ', ' + cityStateZipConcated;
  }

  //#region => Submit Form to Server with transformation
  // Called from routeToStep to transform the forms into Model and pass to server
  TransformModel(step: WizardStep) {
    // Server entity is different, so transforming the information filled in by user before passig it to server
    // MainModel is filled from steps responses and MainModel is passed to server
    this.MainModel.id = 0;
    this.MainModel.cityId = this.lookupSvc.serviceCity[0].id;
    this.MainModel.zipCode = this.serviceAddress.zip;
    this.MainModel.stateCd = (this.lookupSvc.states.filter(x => x.id === this.serviceAddress.state.short_name))[0].id;
    this.MainModel.companyCd = this.lookupSvc.companies[this.serviceAddress.state.short_name].id;
    this.MainModel.recCreateUsrId = this.steps[StepsIdEnum.INFORMATION].form.value.firstName
      + ' ' + this.steps[StepsIdEnum.INFORMATION].form.value.lastName;
    this.MainModel.recUpdateUsrId = this.steps[StepsIdEnum.INFORMATION].form.value.firstName
      + ' ' + this.steps[StepsIdEnum.INFORMATION].form.value.lastName;
    // If google is not having county from map, then pass -1, else filter the county
    if (this.lookupSvc.serviceCounty.length > 0 && this.serviceAddress.county) {
      const county =   this.lookupSvc.serviceCounty.filter(x => x.itemName.toUpperCase()
      === this.serviceAddress.county.toUpperCase())[0];
      this.MainModel.countyId = county !== undefined ? county.id : '-1';
    } else {
      this.MainModel.countyId = '-1';
    }
    // If google is not having local District from map, then pass -1, else filter the local district
    if (this.serviceAddress.localDistrictCd) {
      this.MainModel.localDistrictCd = this.serviceAddress.localDistrictCd;
    } else {
      this.MainModel.localDistrictCd = null;
    }
    this.MainModel.grStatus = this.constantsSvc.grStatusMultistep;
    this.MainModel.grStatusUpdatedBy = this.steps[StepsIdEnum.INFORMATION].form.value.firstName
      + ' ' + this.steps[StepsIdEnum.INFORMATION].form.value.lastName;
    this.MainModel.createDate = this.MainModel.grStatusUpdatedDate =
    this.datePipe.transform(new Date(), 'dd-MMM-yy HH:mm:ss', this.constantsSvc.TimeZone);
    this.MainModel.applicantName = this.steps[StepsIdEnum.INFORMATION].form.value.firstName
      + ' ' + this.steps[StepsIdEnum.INFORMATION].form.value.lastName;
    this.MainModel.updatedBy = this.steps[StepsIdEnum.INFORMATION].form.value.firstName
      + ' ' + this.steps[StepsIdEnum.INFORMATION].form.value.lastName;
    this.MainModel.enteredBy = this.MainModel.applicantName;
    this.MainModel.recCreateUsrId = this.MainModel.applicantName;
    this.MainModel.recUpdateUsrId = this.MainModel.applicantName;
    this.MainModel.development = this.steps[StepsIdEnum.ADDRESSES].form.value.serviceDevelopementName;
    this.MainModel.landDistrict = this.steps[StepsIdEnum.ADDRESSES].form.value.serviceLanddistrict;
    this.MainModel.landLot = this.steps[StepsIdEnum.ADDRESSES].form.value.serviceLandLot;
    this.MainModel.serviceFormattedAddress = this.getFormattedServiceAddress();
    this.MainModel.mailingFormattedAddress = this.getFormattedMailingAddress();
    this.MainModel.pinFormattedAddress = this.serviceAddress.formatted_address;
    if (this.sessionSvc.paramLdc === CompaniesEnum.GA && this.paramRequestType === RequestTypesEnum.Residential.toUpperCase()) {
      this.MainModel.marketerId = this.steps[StepsIdEnum.PREMISE].form.getRawValue().marketerChoice.id;
      this.MainModel.marketerChosen = this.steps[StepsIdEnum.PREMISE].form.getRawValue().marketerChosen;
    }
    else if (this.sessionSvc.paramLdc === CompaniesEnum.GA && this.paramRequestType === RequestTypesEnum.Commercial.toUpperCase()) {
      this.MainModel.marketerId = this.steps[StepsIdEnum.PREMISE].form.getRawValue().commercialMarketerChoice.id;
      this.MainModel.marketerChosen = this.steps[StepsIdEnum.PREMISE].form.getRawValue().commercialMarketerChosen;
    }
    else {
      this.MainModel.marketerId = null;
      this.MainModel.marketerChosen = null;
    }

    this.MainModel.sourceCd = 'Applicant';
    // MainModel is filled with Applicant Information
    this.MainModel.contacts.push(this.buildContactObj());

    // MainModel is filled with Site Contact Information
    this.buildSiteContactObj();
    this.getBuildingType();

    // MainModel is filled with Service Address
    this.MainModel.addresses.push(this.buildServiceAddressObj());

    // MainModel is filled with Mailing Address
    this.MainModel.addresses.push(this.buildMailAddressObj());

    // MainModel is filled with Customer Address
    this.MainModel.addresses.push(this.buildCustomerAddressObj());

    // MainModel is filled with Premise Information based on property type
    if (this.paramRequestType === RequestTypesEnum.Residential.toUpperCase()) {
      this.buildResTypePremiseObj();
    } else if (this.paramRequestType === RequestTypesEnum.Commercial.toUpperCase()) {
      this.buildComTypePremiseObj();
    } else {
      this.buildResTypePremiseObj();
      this.buildComTypePremiseObj();
      const gasRequestTypeCd = this.getGasTypeCode(this.paramRequestType, '-1', 'NA');
      this.MainModel.gasRqstTpCd = gasRequestTypeCd === null ? this.MainModel.gasRqstTpCd : gasRequestTypeCd;
    }
    const date = (new Date()).getFullYear().toString().substr(-2);
    const address = this.serviceAddress;
    const addressCustomer = this.steps[StepsIdEnum.ADDRESSES].form.getRawValue();
    const sc = this.lookupSvc.serviceCenters.find(x => x.itemName.toUpperCase().includes(address.serviceCenter.toUpperCase()));
    const scCode = sc ? sc.locationId : ' ';
    this.MainModel.projectName = date + this.MainModel.gasRqstTpCd + '-' + scCode + '-' + addressCustomer.customerStreetNumber + ' ' +
    addressCustomer.customerStreetName + ' ' + address.city + ' ' + address.state.short_name + ' ' + address.zip;
    this.MainModel.bcaTypeCd = this.getBcaTypeCode(this.MainModel.gasRqstTpCd);
    // Call add method and pass the mainModel ad route to acknowledge in case request is created
    this.MainModel.premiseId = this.lookupSvc.meterDuplicate.premiseId;

   
    this.MainModel.kmiCommunication=this.steps[StepsIdEnum.INFORMATION].form.value.kmiCommunication; 
    this.MainModel.kmiPreference=this.steps[StepsIdEnum.INFORMATION].form.value.kmiReceive;
    this.MainModel.kmiEmail=this.steps[StepsIdEnum.INFORMATION].form.value.kmiEmailAddress;
    this.MainModel.kmiPhoneNo=this.steps[StepsIdEnum.INFORMATION].form.value.kmiTextNumber;
    this.MainModel.kmiFrequency=this.steps[StepsIdEnum.INFORMATION].form.value.kmiFrequency;
  

    const subscription = this.add(this.MainModel).subscribe(result => {
     if (subscription) {
      subscription.unsubscribe();
     }
      if (result.statusCode === 201) {
        this.MainModel = result.data;
        this.router.navigate([this.sessionSvc.paramLdc.toLowerCase(), 'applicant',
        this.paramRequestType.toLocaleLowerCase(), step.route]);
      } else {
        this.MainModel = new GasRequestModel();
        swal(this.constantsSvc.errorSwal1, this.constantsSvc.contactCustomerCareSwal, this.constantsSvc.errorSwal);
      }
    }, result => {
      this.MainModel = new GasRequestModel();
      swal(this.constantsSvc.errorSwal1, this.constantsSvc.contactCustomerCareSwal, this.constantsSvc.errorSwal);
    });
  }
  // Code to Get Building Type
  getBuildingType() {
    const buildingTypeObj = {
      id: 0,
      gasRequestId: 0,
      questionId: 0,
      questionResponse: null,
      recordCreatedBy: this.MainModel.applicantName,
      sequenceId: 13,
      recUpdateUsrId: this.MainModel.applicantName,
      recUpdateDttm: this.datePipe.transform(new Date(), 'dd-MMM-yy HH:mm:ss'),
      recCreateUsrId: this.MainModel.applicantName,
      recordCreatedDateTime: this.datePipe.transform(new Date(), 'dd-MMM-yy HH:mm:ss'),
      panelNumber: '1',
      gasApplianceQuestSummaries: []
    };
    if (this.steps[StepsIdEnum.TYPE].form.value.question1 === this.constantsSvc.multi_Family1) {
      buildingTypeObj.questionId = 56;
      buildingTypeObj.questionResponse = this.constantsSvc.multiFamilyBuildingType;
    } else if (this.steps[StepsIdEnum.TYPE].form.value.question1 === this.constantsSvc.Residential && (
      this.steps[StepsIdEnum.TYPE].form.value.question5 === 'Yes' || this.steps[StepsIdEnum.TYPE].form.value.question5 === 'No')) {
      if (this.steps[StepsIdEnum.TYPE].form.value.question6 === this.constantsSvc.single_Family) {
        buildingTypeObj.questionId = 56;
        buildingTypeObj.questionResponse = this.constantsSvc.singleFamilyBuildingType;
      } else if (this.steps[StepsIdEnum.TYPE].form.value.question6 === this.constantsSvc.mobile_Home) {
        buildingTypeObj.questionId = 56;
        buildingTypeObj.questionResponse = this.constantsSvc.mobileHomeBuildingType;
      }
    } else if (this.steps[StepsIdEnum.TYPE].form.value.question16 === this.constantsSvc.Industrial1) {
      buildingTypeObj.questionId = 57;
      buildingTypeObj.questionResponse = this.constantsSvc.industrialBuildingType;
    }
    this.MainModel.gasRequestTypeAndPremiseDetails.push(buildingTypeObj);
  }
  // MainModel is filled with Applicant Information in server expected format
  buildContactObj() {

    const ContactPhObj = {
      id: 0,
      contactId: 0,
      phoneNumber: this.steps[StepsIdEnum.INFORMATION].form.value.applicantPhoneNumber,
      extension: this.steps[StepsIdEnum.INFORMATION].form.value.applicantPhoneNumberExtension,
      recCreateUsrId: this.MainModel.applicantName,
      recUpdateUsrId: this.MainModel.applicantName,
      recCreateDttm: this.datePipe.transform(new Date(), 'dd-MMM-yy HH:mm:ss'),
      recUpdateDttm: this.datePipe.transform(new Date(), 'dd-MMM-yy HH:mm:ss')
    };

    const ContactObj = {
      id: 0,
      firstName: this.steps[StepsIdEnum.INFORMATION].form.value.firstName,
      middleInitial: this.steps[StepsIdEnum.INFORMATION].form.value.middleInitial,
      emailAddress: this.steps[StepsIdEnum.INFORMATION].form.value.emailAddress,
      companyName: this.steps[StepsIdEnum.INFORMATION].form.value.companyName,
      lastName: this.steps[StepsIdEnum.INFORMATION].form.value.lastName,
      recCreateUsrId: this.MainModel.applicantName,
      recUpdateUsrId: this.MainModel.applicantName,
      recCreateDttm: this.datePipe.transform(new Date(), 'dd-MMM-yy HH:mm:ss'),
      recUpdateDttm: this.datePipe.transform(new Date(), 'dd-MMM-yy HH:mm:ss'),
      contactTypeCode: 'A',
      communicationTypeCode: 'E',
      gasRequestId: 0,
      siteContactType: 'NA',
      contactPhoneNumbers: [],
      siteContactTypeOthers: null,
    };

    ContactObj.contactPhoneNumbers.push(ContactPhObj);

    if (this.steps[StepsIdEnum.INFORMATION].form.value.applicantPhoneNumber1 !== undefined
      && this.steps[StepsIdEnum.INFORMATION].form.value.applicantPhoneNumber1 !== '') {
      const ContactPhObj1 = {
        id: 0,
        contactId: 0,
        phoneNumber: this.steps[StepsIdEnum.INFORMATION].form.value.applicantPhoneNumber1,
        extension: this.steps[StepsIdEnum.INFORMATION].form.value.applicantPhoneNumberExtension,
        recCreateUsrId: this.MainModel.applicantName,
        recUpdateUsrId: this.MainModel.applicantName,
        recCreateDttm: this.datePipe.transform(new Date(), 'dd-MMM-yy HH:mm:ss'),
        RecUpdateDttm: this.datePipe.transform(new Date(), 'dd-MMM-yy HH:mm:ss')
      };
      ContactObj.contactPhoneNumbers.push(ContactPhObj1);
    }
    if (this.steps[StepsIdEnum.INFORMATION].form.value.applicantPhoneNumber2 !== undefined
      && this.steps[StepsIdEnum.INFORMATION].form.value.applicantPhoneNumber2 !== '') {
      const ContactPhObj2 = {
        id: 0,
        contactId: 0,
        phoneNumber: this.steps[StepsIdEnum.INFORMATION].form.value.applicantPhoneNumber2,
        extension: '',
        recCreateUsrId: this.MainModel.applicantName,
        recUpdateUsrId: this.MainModel.applicantName,
        recCreateDttm: this.datePipe.transform(new Date(), 'dd-MMM-yy HH:mm:ss'),
        RecUpdateDttm: this.datePipe.transform(new Date(), 'dd-MMM-yy HH:mm:ss')
      };
      ContactObj.contactPhoneNumbers.push(ContactPhObj2);
    }
    if (this.steps[StepsIdEnum.INFORMATION].form.value.applicantPhoneNumber3 !== undefined
      && this.steps[StepsIdEnum.INFORMATION].form.value.applicantPhoneNumber3 !== '') {
      const ContactPhObj3 = {
        id: 0,
        contactId: 0,
        phoneNumber: this.steps[StepsIdEnum.INFORMATION].form.value.applicantPhoneNumber3,
        extension: '',
        recCreateUsrId: this.MainModel.applicantName,
        recUpdateUsrId: this.MainModel.applicantName,
        recCreateDttm: this.datePipe.transform(new Date(), 'dd-MMM-yy HH:mm:ss'),
        RecUpdateDttm: this.datePipe.transform(new Date(), 'dd-MMM-yy HH:mm:ss')
      };
      ContactObj.contactPhoneNumbers.push(ContactPhObj3);
    }
    return ContactObj;
  }
  // MainModel is filled with Site Contact Information in server expected format
  buildSiteContactObj() {
    for (let i = 0; i < this.steps[StepsIdEnum.INFORMATION].form.value.contactTypeDetails.length; i++) {
      const SiteContactObj = {
        id: 0,
        firstName: this.steps[StepsIdEnum.INFORMATION].form.value.contactTypeDetails[i].firstName,
        middleInitial: null,
        emailAddress: this.steps[StepsIdEnum.INFORMATION].form.value.contactTypeDetails[i].emailAddress,
        companyName: this.steps[StepsIdEnum.INFORMATION].form.value.contactTypeDetails[i].companyName,
        lastName: this.steps[StepsIdEnum.INFORMATION].form.value.contactTypeDetails[i].lastName,
        contactTypeCode: 'S',
        communicationTypeCode: 'E',
        gasRequestId: 0,
        recCreateUsrId: this.MainModel.applicantName,
        recUpdateUsrId: this.MainModel.applicantName,
        recCreateDttm: this.datePipe.transform(new Date(), 'dd-MMM-yy HH:mm:ss'),
        recUpdateDttm: this.datePipe.transform(new Date(), 'dd-MMM-yy HH:mm:ss'),
        siteContactType: this.steps[StepsIdEnum.INFORMATION].form.value.contactTypeDetails[i].siteContactType.id,
        siteContactTypeOthers: this.steps[StepsIdEnum.INFORMATION].form.value.contactTypeDetails[i].siteContactType.itemName
          === 'Other' ? this.steps[StepsIdEnum.INFORMATION].form.value.contactTypeDetails[i].siteContactTypeOthers : null,
        contactPhoneNumbers: [
          {
            id: 0,
            contactId: 0,
            phoneNumber: this.steps[StepsIdEnum.INFORMATION].form.value.contactTypeDetails[i].siteContactPhoneNumber,
            extension: this.steps[StepsIdEnum.INFORMATION].form.value.contactTypeDetails[i].siteContactPhoneNumberExtension,
            recCreateUsrId: this.MainModel.applicantName,
            recUpdateUsrId: this.MainModel.applicantName,
            recCreateDttm: this.datePipe.transform(new Date(), 'dd-MMM-yy HH:mm:ss'),
            recUpdateDttm: this.datePipe.transform(new Date(), 'dd-MMM-yy HH:mm:ss')
          }
        ]
      };

      this.MainModel.contacts.push(SiteContactObj);
    }
  }
  // MainModel is filled with Service Address in server expected format
  buildServiceAddressObj() {
    return {
      id: 0,
      gasRequestId: 0,
      addressTypeCode: 'P',
      isPoBox: 'N',
      poBoxNumber: null,
      latitude: parseFloat(parseFloat(this.serviceAddress.latitude).toFixed(5)),
      longitude: parseFloat(parseFloat(this.serviceAddress.longitude).toFixed(5)),
      xCoordinate: parseFloat(parseFloat(this.serviceAddress.xCoordinate).toFixed(5)),
      yCoordinate: parseFloat(parseFloat(this.serviceAddress.yCoordinate).toFixed(5)),
      streetNumber: this.steps[StepsIdEnum.ADDRESSES].form.getRawValue().serviceStreetNumber,
      streetName: this.steps[StepsIdEnum.ADDRESSES].form.getRawValue().serviceStreetName,
      unitNumber: this.steps[StepsIdEnum.ADDRESSES].form.getRawValue().serviceUnitNumber,
      cityId: this.steps[StepsIdEnum.ADDRESSES].form.getRawValue().serviceCity.id,
      zip: this.steps[StepsIdEnum.ADDRESSES].form.getRawValue().serviceZip,
      stateCd: this.steps[StepsIdEnum.ADDRESSES].form.getRawValue().serviceState.id,
      countyId: this.GetServiceCounty(),
      recCreateUsrId: this.MainModel.applicantName,
      recUpdateUsrId: this.MainModel.applicantName,
      recCreateDttm: this.datePipe.transform(new Date(), 'dd-MMM-yy HH:mm:ss'),
      recUpdateDttm: this.datePipe.transform(new Date(), 'dd-MMM-yy HH:mm:ss')
    };
  }
  // To get Service county from address
  GetServiceCounty() {
    const rawFormData = this.steps[StepsIdEnum.ADDRESSES].form.getRawValue();
    if (rawFormData != null && rawFormData !== undefined)    {
      this.serviceCounty = rawFormData.serviceCounty;
      if (this.serviceCounty  != null && this.serviceCounty  !== undefined) {
      return this.serviceCounty.id;
      } else {
        return '-1';
      }
    }
  }
  // MainModel is filled with Mailing Address in server expected format
  buildMailAddressObj() {
    const rawFormData = this.steps[StepsIdEnum.ADDRESSES].form.getRawValue();
    return {
      id: 0,
      gasRequestId: 0,
      addressTypeCode: 'M',
      isPoBox: rawFormData.isPoBox ? 'Y' : 'N',
      poBoxNumber: rawFormData.isPoBox ? rawFormData.poBoxAddress : null,
      latitude: 0,
      longitude: 0,
      streetNumber: rawFormData.mailingStreetNumber,
      streetName: rawFormData.mailingStreetName,
      unitNumber: rawFormData.mailingUnitNumber,
      cityId: rawFormData.mailingCity.id,
      zip: rawFormData.mailingZip,
      stateCd: rawFormData.mailingState.id,
      countyId: this.GetServiceCounty(),
      recCreateUsrId: this.MainModel.applicantName,
      recUpdateUsrId: this.MainModel.applicantName,
      recCreateDttm: this.datePipe.transform(new Date(), 'dd-MMM-yy HH:mm:ss'),
      recUpdateDttm: this.datePipe.transform(new Date(), 'dd-MMM-yy HH:mm:ss')
    };
  }
  // MainModel is filled with Service Address in server expected format
  buildCustomerAddressObj() {
    return {
      id: 0,
      gasRequestId: 0,
      addressTypeCode: 'S',
      isPoBox: 'N',
      poBoxNumber: null,
      latitude: parseFloat(parseFloat(this.serviceAddress.latitude).toFixed(5)),
      longitude: parseFloat(parseFloat(this.serviceAddress.longitude).toFixed(5)),
      streetNumber: this.steps[StepsIdEnum.ADDRESSES].form.getRawValue().customerStreetNumber,
      streetName: this.steps[StepsIdEnum.ADDRESSES].form.getRawValue().customerStreetName,
      unitNumber: this.steps[StepsIdEnum.ADDRESSES].form.getRawValue().serviceUnitNumber,
      cityId: this.steps[StepsIdEnum.ADDRESSES].form.getRawValue().serviceCity.id,
      zip: this.steps[StepsIdEnum.ADDRESSES].form.getRawValue().serviceZip,
      stateCd: this.steps[StepsIdEnum.ADDRESSES].form.getRawValue().serviceState.id,
      countyId: this.GetServiceCounty(),
      recCreateUsrId: this.MainModel.applicantName,
      recUpdateUsrId: this.MainModel.applicantName,
      recCreateDttm: this.datePipe.transform(new Date(), 'dd-MMM-yy HH:mm:ss'),
      recUpdateDttm: this.datePipe.transform(new Date(), 'dd-MMM-yy HH:mm:ss')
    };
  }
  // MainModel is filled with Type and Premise Information in server expected format - residential
  buildResTypePremiseObj() {
    const questionList = this.lookupSvc.gasQuestionnaires.filter(i => i.isResidential === 'Y');
    this.getResiQuestionsList();
    if (this.paramRequestType.toUpperCase() !== RequestTypesEnum.MixedUse.toUpperCase()) {
      for (let i = 0; i < this.restypeQuestions.length; i++) {
        for (let j = 0; j < questionList.length; j++) {
          if (this.restypeQuestions[i].question === questionList[j].itemName) {
            const resiTypeObj = {
              id: 0,
              gasRequestId: 0,
              questionId: questionList[j].id,
              questionResponse: this.restypeQuestions[i].answer,
              recordCreatedBy: this.MainModel.applicantName,
              sequenceId: questionList[j].sequenceId,
              recUpdateUsrId: this.MainModel.applicantName,
              recUpdateDttm: this.datePipe.transform(new Date(), 'dd-MMM-yy HH:mm:ss'),
              recCreateUsrId: this.MainModel.applicantName,
              recordCreatedDateTime: this.datePipe.transform(new Date(), 'dd-MMM-yy HH:mm:ss'),
              panelNumber: '1',
              gasApplianceQuestSummaries: []
            };
            this.MainModel.gasRequestTypeAndPremiseDetails.push(resiTypeObj);
            const gasRequestTypeCd = this.getGasTypeCode(this.steps[StepsIdEnum.TYPE].form.value.question1, questionList[j].id.toString(),
              this.restypeQuestions[i].answer);
            this.MainModel.gasRqstTpCd = gasRequestTypeCd === null ? this.MainModel.gasRqstTpCd : gasRequestTypeCd;

            break;
          }
        }
      }
    }
    for (let i = 0; i < this.resPremiseQuestions.length; i++) {
      for (let j = 0; j < questionList.length; j++) {
        if (this.resPremiseQuestions[i].question === questionList[j].itemName) {
          const resiTypeObj = {
            id: 0,
            gasRequestId: 0,
            questionId: questionList[j].id,
            questionResponse: this.resPremiseQuestions[i].answer,
            recordCreatedBy: this.MainModel.applicantName,
            recUpdateUsrId: this.MainModel.applicantName,
            recUpdateDttm: this.datePipe.transform(new Date(), 'dd-MMM-yy HH:mm:ss'),
            recCreateUsrId: this.MainModel.applicantName,
            recordCreatedDateTime: this.datePipe.transform(new Date(), 'dd-MMM-yy HH:mm:ss'),
            sequenceId: questionList[j].sequenceId,
            panelNumber: '1',
            gasApplianceQuestSummaries: []
          };
          if (this.resPremiseQuestions[i].question === this.constantsSvc.NaturalGasInstlTypeQues) {
            for (let k = 0; k < this.steps[StepsIdEnum.PREMISE].form.value.appliancesforResidential.length; k++) {
              resiTypeObj.gasApplianceQuestSummaries.push({
                id: this.steps[StepsIdEnum.PREMISE].form.value.appliancesforResidential[k].id,
                gasRequestId: this.steps[StepsIdEnum.PREMISE].form.value.appliancesforResidential[k].gasRequestId,
                questionId: questionList[j].id,
                sequenceId: questionList[j].sequenceId,
                applianceId: this.steps[StepsIdEnum.PREMISE].form.value.appliancesforResidential[k].defaultAppliance.id,
                quantityValue: this.steps[StepsIdEnum.PREMISE].form.value.appliancesforResidential[k].quantity,
                loadValue: this.steps[StepsIdEnum.PREMISE].form.value.appliancesforResidential[k].defaultValue,
                recordCreatedBy: this.MainModel.applicantName,
                recUpdateUsrId: this.MainModel.applicantName,
                recUpdateDttm: this.datePipe.transform(new Date(), 'dd-MMM-yy HH:mm:ss'),
                recordCreatedDateTime: this.datePipe.transform(new Date(), 'dd-MMM-yy HH:mm:ss'),
                applianceOthers: this.steps[StepsIdEnum.PREMISE].form.value.appliancesforResidential[k].defaultAppliance.itemName ===
                  'OTHER' ? this.steps[StepsIdEnum.PREMISE].form.value.appliancesforResidential[k].defaultApplianceOther : null,
                existingLoad: this.steps[StepsIdEnum.PREMISE].form.value.appliancesforResidential[k].existingLoad === true ? 'Y' : 'N',
                panelNumber: '1',
              });
            }
          }
          this.MainModel.gasRequestTypeAndPremiseDetails.push(resiTypeObj);
          break;
        }
      }
    }
  }
  // Building the question and answers for residential - Type and Premise Information
  getResiQuestionsList() {
    this.resPremiseQuestions.push({
      question: this.constantsSvc.HomeSqFtQues,
      answer: this.steps[StepsIdEnum.PREMISE].form.value.residentialHomeSqFt.toString(),
      requestType: 'Residential',
      panelNumber: '1'
    });
    this.resPremiseQuestions.push({
      question: this.constantsSvc.ApproxDistFtQues,
      answer: this.steps[StepsIdEnum.PREMISE].form.value.residentialApproxDistanceFt == null ? '' :
        this.steps[StepsIdEnum.PREMISE].form.value.residentialApproxDistanceFt.toString(),
      requestType: 'Residential',
      panelNumber: '1'
    });
    this.resPremiseQuestions.push({
      question: this.constantsSvc.DeliveryPressureReqQues,
      answer: this.steps[StepsIdEnum.PREMISE].form.value.residentialDeliveryPressure.id,
      requestType: 'Residential',
      panelNumber: '1'
    });
    this.resPremiseQuestions.push({
      question: this.constantsSvc.totalUnits,
      answer: this.steps[StepsIdEnum.PREMISE].form.value.residentialNoOfUnit,
      requestType: 'Residential',
      panelNumber: '1'
    });
    this.resPremiseQuestions.push({
      question: this.constantsSvc.NaturalGasInstlTypeQues,
      answer: null,
      requestType: 'Residential',
      panelNumber: '1'
    });
    this.resPremiseQuestions.push({
      question: this.constantsSvc.constructionStatusQues,
      answer: this.steps[StepsIdEnum.PREMISE].form.value.residentialStatusofConstruction !== null ?
        this.steps[StepsIdEnum.PREMISE].form.value.residentialStatusofConstruction.id : null,
      requestType: 'Residential',
      panelNumber: '1'
    });
    this.resPremiseQuestions.push({
      question: this.constantsSvc.UnitDescription,
      answer: this.steps[StepsIdEnum.PREMISE].form.value.residentialUnitDescription,
      requestType: 'Residential',
      panelNumber: '1'
    });
    this.resPremiseQuestions.push({
      question: this.constantsSvc.needingServiceQues,
      answer: this.steps[StepsIdEnum.PREMISE].form.value.residentialNeedingServiceDate.toString(),
      requestType: 'Residential',
      panelNumber: '1'
    });
    this.resPremiseQuestions.push({
      question: this.constantsSvc.propertyOwnerQues,
      answer: this.steps[StepsIdEnum.PREMISE].form.value.propertyOwner,
      requestType: 'Residential',
      panelNumber: '1'
    });
    if (this.steps[StepsIdEnum.PREMISE].form.value.residentialDeliveryPressure.itemName === 'Others') {
      this.resPremiseQuestions.push({
        question: this.constantsSvc.describeOthersQues,
        answer: this.steps[StepsIdEnum.PREMISE].form.value.residentialDeliveryPressureOthers,
        requestType: 'Residential',
        panelNumber: '1'
      });
    }

    if (this.steps[StepsIdEnum.PREMISE].form.value.marketerChosen != null) {
      this.resPremiseQuestions.push({
        question: this.constantsSvc.marketerChosenQues,
        answer: this.steps[StepsIdEnum.PREMISE].form.value.marketerChosen,
        requestType: 'Residential',
        panelNumber: '1'
      });
    }


    if (!isNullOrUndefined(this.steps[StepsIdEnum.PREMISE].form.getRawValue().marketerChoice)){
      this.resPremiseQuestions.push({
        question: this.constantsSvc.marketerChoiceQues,
        answer: this.steps[StepsIdEnum.PREMISE].form.getRawValue().marketerChoice.itemName,
        requestType: 'Residential',
        panelNumber: '1'
      });
    }

    if (this.steps[StepsIdEnum.TYPE].form.value.question62 !== null) {
       this.restypeQuestions.push({
         question: this.constantsSvc.licensedBuilderQues,
         answer: this.steps[StepsIdEnum.TYPE].form.value.question62,
         requestType: 'Residential',
         panelNumber: '1'
       });
    }

    if (this.steps[StepsIdEnum.TYPE].form.value.question1 !== null) {
      this.restypeQuestions.push({
        question: this.constantsSvc.typeofApplicantRequestQues,
        answer: this.steps[StepsIdEnum.TYPE].form.value.question1,
        requestType: 'Residential',
        panelNumber: '1'
      });
    }

    if (this.steps[StepsIdEnum.TYPE].form.value.question2 !== null) {
      this.restypeQuestions.push({
        question: this.constantsSvc.meterOutsideHomeQues,
        answer: this.steps[StepsIdEnum.TYPE].form.value.question2,
        requestType: 'Residential',
        panelNumber: '1'
      });
    }

    if (this.steps[StepsIdEnum.TYPE].form.value.question3 !== null) {
      this.restypeQuestions.push({
        question: this.constantsSvc.additionalMeterQues,
        answer: this.steps[StepsIdEnum.TYPE].form.value.question3,
        requestType: 'Residential',
        panelNumber: '1'
      });
    }

    if (this.steps[StepsIdEnum.TYPE].form.value.question4 !== null) {
      this.restypeQuestions.push({
        question: this.constantsSvc.addReplacemeterQues,
        answer: this.steps[StepsIdEnum.TYPE].form.value.question4,
        requestType: 'Residential',
        panelNumber: '1'
      });
    }

    if (this.steps[StepsIdEnum.TYPE].form.value.question5 !== null) {
      this.restypeQuestions.push({
        question: this.constantsSvc.newConstructionQues,
        answer: this.steps[StepsIdEnum.TYPE].form.value.question5,
        requestType: 'Residential',
        panelNumber: '1'
      });
    }

    if (this.steps[StepsIdEnum.TYPE].form.value.question6 !== null) {
      this.restypeQuestions.push({
        question: this.constantsSvc.typeofHomeQues,
        answer: this.steps[StepsIdEnum.TYPE].form.value.question6,
        requestType: 'Residential',
        panelNumber: '1'
      });
    }

    if (this.steps[StepsIdEnum.TYPE].form.value.question7 !== null) {
      this.restypeQuestions.push({
        question: this.constantsSvc.currentEnergySourceQues,
        answer: this.steps[StepsIdEnum.TYPE].form.value.question7,
        requestType: 'Residential',
        panelNumber: '1'
      });
    }

    if (this.steps[StepsIdEnum.TYPE].form.value.question40 !== null) {
      this.restypeQuestions.push({
        question: this.constantsSvc.meterOutsideBuildingQues,
        answer: this.steps[StepsIdEnum.TYPE].form.value.question40,
        requestType: 'Residential',
        panelNumber: '1'
      });
    }

    if (this.steps[StepsIdEnum.TYPE].form.value.question60 !== null) {
      this.restypeQuestions.push({
        question: this.constantsSvc.multiFamilyHome,
        answer: this.steps[StepsIdEnum.TYPE].form.value.question60,
        requestType: 'Residential',
        panelNumber: '1'
      });
    }

  }
  // MainModel is filled with Type and Premise Information in server expected format - commercial
  buildComTypePremiseObj() {
    const questionList = this.lookupSvc.gasQuestionnaires.filter(i => i.isCommercial === 'Y');
    this.getCommQuestionsList();
    if (this.paramRequestType.toUpperCase() !== RequestTypesEnum.MixedUse.toUpperCase()) {
      for (let i = 0; i < this.comtypeQuestions.length; i++) {
        for (let j = 0; j < questionList.length; j++) {
          if (this.comtypeQuestions[i].question === questionList[j].itemName) {
            const commTypeObj = {
              id: 0,
              gasRequestId: 0,
              questionId: questionList[j].id,
              questionResponse: this.comtypeQuestions[i].answer,
              recordCreatedBy: this.MainModel.applicantName,
              recUpdateUsrId: this.MainModel.applicantName,
              recUpdateDttm: this.datePipe.transform(new Date(), 'dd-MMM-yy HH:mm:ss'),
              recCreateUsrId: this.MainModel.applicantName,
              recordCreatedDateTime: this.datePipe.transform(new Date(), 'dd-MMM-yy HH:mm:ss'),
              sequenceId: questionList[j].sequenceId,
              panelNumber: '1',
              gasApplianceQuestSummaries: []
            };
            this.MainModel.gasRequestTypeAndPremiseDetails.push(commTypeObj);
            const gasRequestTypeCd = this.getGasTypeCode(this.steps[StepsIdEnum.TYPE].form.value.question16, questionList[j].id.toString(),
              this.comtypeQuestions[i].answer);
            this.MainModel.gasRqstTpCd = gasRequestTypeCd === null ? this.MainModel.gasRqstTpCd : gasRequestTypeCd;
            break;
          }
        }
      }
    }

    for (let i = 0; i < this.comPremiseQuestions.length; i++) {
      for (let j = 0; j < questionList.length; j++) {
        if (this.comPremiseQuestions[i].question === questionList[j].itemName) {
          const commTypeObj = {
            id: 0,
            gasRequestId: 0,
            questionId: questionList[j].id,
            questionResponse: this.comPremiseQuestions[i].answer,
            recordCreatedBy: this.MainModel.applicantName,
            recUpdateUsrId: this.MainModel.applicantName,
            recUpdateDttm: this.datePipe.transform(new Date(), 'dd-MMM-yy HH:mm:ss'),
            recCreateUsrId: this.MainModel.applicantName,
            recordCreatedDateTime: this.datePipe.transform(new Date(), 'dd-MMM-yy HH:mm:ss'),
            sequenceId: questionList[j].sequenceId,
            panelNumber: '1',
            gasApplianceQuestSummaries: []
          };
          if (this.comPremiseQuestions[i].question === this.constantsSvc.NaturalGasInstlTypeBusinessQues) {
            for (let k = 0; k < this.steps[StepsIdEnum.PREMISE].form.value.appliancesforCommercial.length; k++) {
              commTypeObj.gasApplianceQuestSummaries.push({
                id: this.steps[StepsIdEnum.PREMISE].form.value.appliancesforCommercial[k].id,
                gasRequestId: this.steps[StepsIdEnum.PREMISE].form.value.appliancesforCommercial[k].gasRequestId,
                questionId: questionList[j].id,
                sequenceId: questionList[j].sequenceId,
                applianceId: this.steps[StepsIdEnum.PREMISE].form.value.appliancesforCommercial[k].defaultAppliance.id,
                quantityValue: this.steps[StepsIdEnum.PREMISE].form.value.appliancesforCommercial[k].quantity,
                loadValue: this.steps[StepsIdEnum.PREMISE].form.value.appliancesforCommercial[k].defaultValue,
                recordCreatedBy: this.MainModel.applicantName,
                recUpdateUsrId: this.MainModel.applicantName,
                recUpdateDttm: this.datePipe.transform(new Date(), 'dd-MMM-yy HH:mm:ss'),
                recordCreatedDateTime: this.datePipe.transform(new Date(), 'dd-MMM-yy HH:mm:ss'),
                applianceOthers:
                  (this.steps[StepsIdEnum.PREMISE].form.value.appliancesforCommercial[k].defaultAppliance.itemName === 'OTHER' ||
                    this.steps[StepsIdEnum.PREMISE].form.value.appliancesforCommercial[k].defaultAppliance.itemName
                    === 'OTHER(NO ALLOWABLE)') ?
                    this.steps[StepsIdEnum.PREMISE].form.value.appliancesforCommercial[k].defaultApplianceOther : null,
                existingLoad: this.steps[StepsIdEnum.PREMISE].form.value.appliancesforCommercial[k].existingLoad === true ? 'Y' : 'N',
                panelNumber: '1'
              });
            }
          }
          this.MainModel.gasRequestTypeAndPremiseDetails.push(commTypeObj);
          break;
        }
      }
    }
  }
  // Building the question and answers for commercial - Type and Premise Information
  getCommQuestionsList() {
    this.comPremiseQuestions.push({
      question: this.constantsSvc.HomeSqFtBusinessQues,
      answer: this.steps[StepsIdEnum.PREMISE].form.value.commercialHomeSqFt.toString(),
      requestType: 'Commercial',
      panelNumber: '1'
    });
    this.comPremiseQuestions.push({
      question: this.constantsSvc.ApproxDistBusinessFtQues,
      answer: this.steps[StepsIdEnum.PREMISE].form.value.commercialApproxDistanceFt == null ? '' :
        this.steps[StepsIdEnum.PREMISE].form.value.commercialApproxDistanceFt.toString(),
      requestType: 'Commercial',
      panelNumber: '1'
    });
    this.comPremiseQuestions.push({
      question: this.constantsSvc.DeliveryPressureReqQues,
      answer: this.steps[StepsIdEnum.PREMISE].form.value.commercialDeliveryPressure.id,
      requestType: 'Commercial',
      panelNumber: '1'
    });
    this.comPremiseQuestions.push({
      question: this.constantsSvc.NaturalGasInstlTypeBusinessQues,
      answer: null,
      requestType: 'Commercial',
      panelNumber: '1'
    });
    this.comPremiseQuestions.push({
      question: this.constantsSvc.constructionStatusQues,
      answer: this.steps[StepsIdEnum.PREMISE].form.value.commercialStatusofConstruction !== null ?
        this.steps[StepsIdEnum.PREMISE].form.value.commercialStatusofConstruction.id : null,
      requestType: 'Commercial',
      panelNumber: '1'
    });
    this.comPremiseQuestions.push({
      question: this.constantsSvc.UnitDescription,
      answer: this.steps[StepsIdEnum.PREMISE].form.value.commercialUnitDescription,
      requestType: 'Commercial',
      panelNumber: '1'
    });
    this.comPremiseQuestions.push({
      question: this.constantsSvc.needingServiceQues,
      answer: this.steps[StepsIdEnum.PREMISE].form.value.commercialNeedingServiceDate.toString(),
      requestType: 'Commercial',
      panelNumber: '1'
    });

    if (this.steps[StepsIdEnum.PREMISE].form.value.commercialDeliveryPressure.itemName === 'Others') {
      this.comPremiseQuestions.push({
        question: this.constantsSvc.describeOthersQues,
        answer: this.steps[StepsIdEnum.PREMISE].form.value.commercialDeliveryPressureOthers,
        requestType: 'Commercial',
        panelNumber: '1'
      });
    }

    if (this.steps[StepsIdEnum.PREMISE].form.value.commercialMarketerChosen != null) {
      this.comPremiseQuestions.push({
        question: this.constantsSvc.marketerChosenQues,
        answer: this.steps[StepsIdEnum.PREMISE].form.value.commercialMarketerChosen,
        requestType: 'Commercial',
        panelNumber: '1'
      });
    }

    if (!isNullOrUndefined(this.steps[StepsIdEnum.PREMISE].form.getRawValue().commercialMarketerChoice)){
      this.comPremiseQuestions.push({
        question: this.constantsSvc.marketerChoiceQues,
        answer: this.steps[StepsIdEnum.PREMISE].form.getRawValue().commercialMarketerChoice.itemName,
        requestType: 'Commercial',
        panelNumber: '1'
      });
    }

    if (this.steps[StepsIdEnum.TYPE].form.value.question62 !== null) {
      this.comPremiseQuestions.push({
        question: this.constantsSvc.licensedBuilderQues,
        answer: this.steps[StepsIdEnum.TYPE].form.value.question62,
        requestType: 'Commercial',
        panelNumber: '1'
      });
   }
    if (this.steps[StepsIdEnum.TYPE].form.value.question16 !== null) {
      this.comtypeQuestions.push({
        question: this.constantsSvc.propertyTypeQues,
        answer: this.steps[StepsIdEnum.TYPE].form.value.question16,
        requestType: 'Commercial',
        panelNumber: '1'
      });
    }

    if (this.steps[StepsIdEnum.TYPE].form.value.question17 !== null) {
      this.comtypeQuestions.push({
        question: this.constantsSvc.meterOutsideBusinessQues,
        answer: this.steps[StepsIdEnum.TYPE].form.value.question17,
        requestType: 'Commercial',
        panelNumber: '1'
      });
    }

    if (this.steps[StepsIdEnum.TYPE].form.value.question18 !== null) {
      this.comtypeQuestions.push({
        question: this.constantsSvc.additionalMeterBusinessQues,
        answer: this.steps[StepsIdEnum.TYPE].form.value.question18,
        requestType: 'Commercial',
        panelNumber: '1'
      });
    }

    if (this.steps[StepsIdEnum.TYPE].form.value.question19 !== null) {
      this.comtypeQuestions.push({
        question: this.constantsSvc.addReplacemeterBusinesQues,
        answer: this.steps[StepsIdEnum.TYPE].form.value.question19,
        requestType: 'Commercial',
        panelNumber: '1'
      });
    }

    if (this.steps[StepsIdEnum.TYPE].form.value.question20 !== null) {
      this.comtypeQuestions.push({
        question: this.constantsSvc.newConstructionQues,
        answer: this.steps[StepsIdEnum.TYPE].form.value.question20,
        requestType: 'Commercial',
        panelNumber: '1'      });
    }

    if (this.steps[StepsIdEnum.TYPE].form.value.question21 !== null) {
      this.comtypeQuestions.push({
        question: this.constantsSvc.currentEnergySourceQues,
        answer: this.steps[StepsIdEnum.TYPE].form.value.question21,
        requestType: 'Commercial',
        panelNumber: '1'
      });
    }
  }
  // Retrieve the type of request user is applying gas for
  getGasTypeCode(PropertyType: string, QuestionId: string, QuestionResponse: string): string {
    // filter the resultset matching propertyType, questionId, questionResponse
    if (PropertyType.toLowerCase() === this.constantsSvc.multi_Family1.toLowerCase())    {
    QuestionId = '16';
    }
    const output = this.lookupSvc.gasRequestType.filter(value =>
      value.propertyType.toUpperCase() === PropertyType.toUpperCase() &&
      value.questionId.toUpperCase() === QuestionId.toUpperCase() &&
      value.questionResponse.toUpperCase() === QuestionResponse.toUpperCase()
    );
    if (output.length > 0) {
      return output[0].typeCode;
    } else {
      return null;
    }
  }
 // Retrieve Bca Type Cd based on Gas request type cd
  getBcaTypeCode(gasRequestTypeCd: string): string {
    const output = this.lookupSvc.gasRequestTypeBcaCode.filter(value =>
      value.id.toUpperCase() === gasRequestTypeCd.toUpperCase());
    if (output.length > 0) {
      return output[0].bcaTypeCode;
    } else {
      return null;
    }
  }
  // WMS Database Call to Web API to add the request with MainModel
  add(model: GasRequestModel): Observable<ApiResult<GasRequestModel>> {
    return this.http.post<GasRequestModel>({
      url: 'Common/AddGasRequest',
      data: model,
      shouldIgnoreInvalidResult: false,
      pleaseWaitTimeoutDuration: 500,
      pleaseWaitMessage: 'Processing.',
      pleaseWaitContainerId: 'ContainerAwaiter'
    });
  }
  // GIS Service Call to Web API to store ga request number in GIS database
  postGasRequestNumber(lat: string, longt: string, stateCd: string, requestNum: string): Observable<ApiResult<GasRequestNumber>> {
    return this.http.post<GasRequestNumber>({
      url: 'externalService' + '/PostGasRequestNumber',
      params: { lat: lat, longt: longt, stateCd: stateCd, requestNum: requestNum }
    });
  }
  //#endregion => Submit Form to Server with transformation
}
